import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { ArrowRight, MapPin, Briefcase, Clock } from 'lucide-react';
import useSEO from '../hooks/useSEO';

const Careers = () => {
    const [jobs, setJobs] = useState(null);

    useSEO({
        title: 'Careers — Join Dry Ice Bulls Cleaning System®',
        description: 'Join the Dry Ice Bulls Cleaning System® team. Industrial cleaning technicians, equipment sales, and support roles based in North Carolina.',
    });

    useEffect(() => {
        api.get('/jobs').then((r) => setJobs(r.data)).catch(() => setJobs([]));
    }, []);

    return (
        <div className="pt-24 pb-24" data-testid="careers-page">
            <div className="container-page">
                <div className="label-caps text-brand-primary mb-4">/ Careers</div>
                <h1 className="font-display font-black uppercase text-5xl md:text-7xl leading-[0.95] text-neutral-900">
                    Build a career in<br /><span className="text-brand-primary">industrial cleaning tech.</span>
                </h1>
                <p className="mt-6 max-w-3xl text-base md:text-lg text-neutral-600 leading-relaxed">
                    We hire technicians, equipment specialists, and sales talent who take pride in solving real industrial problems. Based in North Carolina, serving customers nationwide.
                </p>

                <section className="mt-14">
                    <div className="label-caps text-neutral-500 mb-4 text-xs">/ Open positions</div>
                    {jobs === null ? (
                        <div className="text-neutral-400 label-caps text-sm">Loading…</div>
                    ) : jobs.length === 0 ? (
                        <div className="border border-dashed border-neutral-300 p-8 max-w-3xl">
                            <div className="font-display font-bold uppercase text-xl text-neutral-900 mb-2">No open positions right now.</div>
                            <p className="text-sm text-neutral-600 mb-4">
                                Send us your resume anyway — we&apos;re always looking for great people who love industrial equipment and don&apos;t mind getting hands dirty.
                            </p>
                            <Link
                                to="/contact?subject=Career+Interest"
                                className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white font-bold px-5 py-3 label-caps transition-colors"
                                data-testid="careers-cta-resume"
                            >
                                Send Resume <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {jobs.map((j) => (
                                <div key={j.id} className="border border-neutral-200 bg-white p-6 hover:border-brand-primary transition-colors" data-testid={`job-${j.id}`}>
                                    <div className="label-caps text-brand-primary text-xs mb-2">/ {j.department || 'Team'}</div>
                                    <h3 className="font-display font-black uppercase text-2xl text-neutral-900 leading-tight mb-3">{j.title}</h3>
                                    <div className="flex flex-wrap gap-3 text-xs text-neutral-500 mb-4">
                                        <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{j.location}</span>
                                        <span className="inline-flex items-center gap-1"><Briefcase className="h-3 w-3" />{j.employment_type}</span>
                                        {j.salary && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{j.salary}</span>}
                                    </div>
                                    {j.description && <p className="text-sm text-neutral-600 leading-relaxed mb-4 line-clamp-3">{j.description}</p>}
                                    <Link
                                        to={`/contact?subject=Application: ${encodeURIComponent(j.title)}`}
                                        className="inline-flex items-center gap-2 label-caps text-brand-primary hover:text-neutral-900 transition-colors text-xs"
                                    >
                                        Apply Now <ArrowRight className="h-3.5 w-3.5" />
                                    </Link>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};

export default Careers;
